<template>
    <!--basic setting-->
    <div class="border mb-2 rounded-lg font-weight-bold text-dark" v-if="settings">
        <div class="m-2">
            <div class="nav-link flex align-items-center text-danger">
                <i class="nav-icon fas fa-cog mr-1"></i>
                <div>AdCrew Settings</div>
            </div>
            <ul class="m-3 list-unstyled">
                        <li class="nav-item mb-2 font-weight-normal">
                            <div class="d-flex justify-content-start align-items-center">
                                <div class="col-md-11">
                                    Number of ad requests <small class="text-muted font-weight-normal">(waterfall only)</small>
                                </div>
                                <div class="col-md-1">
                                    <div class="form-group">
                                        <input type="number" min="1" max="1200" class="form-control" v-model="settings.total_ad_requests">
                                    </div>
                                </div>
                            </div>
                        </li>
                        <li class="nav-item mb-2 font-weight-normal">
                            <div class="d-flex justify-content-start align-items-center">
                                <div class="col-md-11">
                                    Delay Between Requests <small class="text-muted font-weight-normal">(waterfall only)</small>
                                </div>
                                <div class="col-md-1">
                                    <div class="form-group">
                                        <input type="number" min="1" max="1200" class="form-control" v-model="settings.delay_between_requests">
                                    </div>
                                </div>
                            </div>
                            <small class="text-muted font-weight-normal d-block mt-1">
                                These settings only apply in <strong>Waterfall</strong> mode, where the player retries ad requests up to the configured limit with a delay between each attempt. In <strong>Scheduled</strong> mode, if a tag fails its break is skipped and content continues to the next break. In <strong>Spread</strong> mode, if a tag fails its break is skipped, but the rotation continues — the next break still gets the next tag in order.
                            </small>
                        </li>

                        <!-- Ad Request Mode -->
                        <li class="nav-item mb-2 font-weight-normal">
                            <div class="border rounded p-3 mb-3">
                                <h6 class="font-weight-bold mb-2">
                                    <i class="fas fa-random mr-1"></i> Ad Request Mode
                                </h6>
                                <div class="d-flex align-items-center mb-3">
                                    <select class="form-control" v-model="settings.ad_request_mode" style="max-width: 280px;">
                                        <option value="waterfall">Waterfall</option>
                                        <option value="scheduled">Scheduled</option>
                                        <option value="spread">Spread</option>
                                    </select>
                                </div>
                                <div class="text-muted font-weight-normal mb-0" style="font-size: 0.85em;">
                                    <p class="mb-1"><strong>Waterfall:</strong> Pre-roll only. Tries the primary ad tag first, then falls back to backup tags in order if it fails. Retries up to the configured request limit.</p>
                                    <p class="mb-1"><strong>Scheduled:</strong> Ads play at specific content percentages. Each break is assigned a dedicated tag by position (break 1 = tag 1, break 2 = tag 2). If there are more breaks than tags, the last tag is reused for all remaining breaks. If a tag fails, that break is skipped.</p>
                                    <p class="mb-0"><strong>Spread:</strong> Ads play at specific content percentages. Tags rotate round-robin across all breaks and wrap around (break 1 = tag 1, break 2 = tag 2, ..., break 5 = tag 1 again). If a tag fails, that break is skipped.</p>
                                </div>
                            </div>
                        </li>
                        <!-- Ad Request Mode End -->

                        <!-- Ad Request Schedule (visible for scheduled and spread modes) -->
                        <li class="nav-item mb-2 font-weight-normal" v-if="settings.ad_request_mode === 'scheduled' || settings.ad_request_mode === 'spread'">
                            <div class="border rounded p-3 mb-3">
                                <h6 class="font-weight-bold mb-2">
                                    <i class="fas fa-clock mr-1"></i> Ad Request Schedule
                                </h6>
                                <p class="text-muted font-weight-normal mb-3" style="font-size: 0.85em;">
                                    Request ads at specific content playback percentages.
                                    <br><strong>0%</strong> = pre-roll, <strong>100%</strong> = post-roll, values in between = mid-rolls.
                                    <br>In <strong>Scheduled</strong> mode, each break maps 1:1 to a tag in order. In <strong>Spread</strong> mode, tags rotate round-robin.
                                </p>

                                <div class="d-flex flex-wrap align-items-center mb-2">
                                    <div 
                                        v-for="(pct, index) in scheduleList" 
                                        :key="index"
                                        class="badge badge-primary d-flex align-items-center mr-2 mb-2 px-3 py-2"
                                        style="font-size: 0.95em;"
                                    >
                                        <span>{{ pct }}%</span>
                                        <a href="#" @click.prevent="removeScheduleEntry(index)" class="ml-2 text-white" style="opacity:0.8;">
                                            <i class="fas fa-times"></i>
                                        </a>
                                    </div>
                                    <span v-if="scheduleList.length === 0" class="text-muted font-weight-normal">
                                        No milestones configured (pre-roll only by default)
                                    </span>
                                </div>

                                <div class="d-flex align-items-center mt-2">
                                    <input 
                                        type="number" 
                                        min="0" 
                                        max="100" 
                                        step="1"
                                        class="form-control mr-2" 
                                        style="max-width: 100px;"
                                        v-model.number="newSchedulePercent"
                                        placeholder="%"
                                        @keyup.enter="addScheduleEntry"
                                    >
                                    <button class="btn btn-sm btn-outline-primary" @click.prevent="addScheduleEntry">
                                        <i class="fas fa-plus"></i> Add
                                    </button>
                                    <button class="btn btn-sm btn-outline-secondary ml-2" @click.prevent="loadDefaultSchedule">
                                        <i class="fas fa-undo"></i> Load Defaults
                                    </button>
                                    <button class="btn btn-sm btn-outline-danger ml-2" @click.prevent="clearSchedule">
                                        <i class="fas fa-trash"></i> Clear All
                                    </button>
                                </div>
                                <small v-if="scheduleError" class="text-danger mt-1 d-block">{{ scheduleError }}</small>
                            </div>
                        </li>
                        <!-- Ad Request Schedule End -->

                        <li class="nav-item mb-2 font-weight-normal">
                            <div class="d-flex justify-content-start align-items-center">
                                <div class="col-md-6">
                                    Debug
                                </div>
                                <div class="col-md-6">
                                    <div class="inline">
                                        <div class="custom-control custom-switch p-0 py-2">
                                            <label class="switch" for="debug">
                                                <input type="checkbox" class="primary" id="debug" v-model="settings.debug">
                                                <span class="slider round"></span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </li>
                        <li>
                            <hr><br>
                            <button class="btn btn-success float-right" @click.prevent="update()">Save</button>
                            <br>
                        </li>
            </ul>
        </div>
    </div>
    <!--basic setting-->
</template>
<script>
import { RepositoryFactory } from '@/services/repositories/PlayerAPI/RepositoryFactory'
const AdCrewSettingsRepo = RepositoryFactory.get('adcrew_settings')

export default {
    props: {
        settings: {
            type:  Object,
        }
    },
    data () {
        return {
            newSchedulePercent: null,
            scheduleError: ''
        }
    },
    computed: {
        scheduleList() {
            if (!this.settings || !Array.isArray(this.settings.ad_request_schedule)) return [0]
            return [...this.settings.ad_request_schedule].sort((a, b) => a - b)
        }
    },
    watch: {},
    methods: {
        addScheduleEntry() {
            this.scheduleError = ''
            const val = Math.round(Number(this.newSchedulePercent))
            if (!Number.isFinite(val) || val < 0 || val > 100) {
                this.scheduleError = 'Enter a value between 0 and 100'
                return
            }
            if (!Array.isArray(this.settings.ad_request_schedule)) {
                this.$set(this.settings, 'ad_request_schedule', [])
            }
            if (this.settings.ad_request_schedule.includes(val)) {
                this.scheduleError = val + '% is already in the schedule'
                return
            }
            this.settings.ad_request_schedule.push(val)
            this.settings.ad_request_schedule.sort((a, b) => a - b)
            this.newSchedulePercent = null
        },
        removeScheduleEntry(index) {
            const sorted = [...this.settings.ad_request_schedule].sort((a, b) => a - b)
            const valueToRemove = sorted[index]
            const realIndex = this.settings.ad_request_schedule.indexOf(valueToRemove)
            if (realIndex !== -1) {
                this.settings.ad_request_schedule.splice(realIndex, 1)
            }
        },
        loadDefaultSchedule() {
            this.$set(this.settings, 'ad_request_schedule', [0, 10, 30, 50, 70, 85, 100])
        },
        clearSchedule() {
            this.$set(this.settings, 'ad_request_schedule', [])
        },
        update () {
            var settings_id = this.settings.id
            delete this.settings['id']
            AdCrewSettingsRepo.patch(settings_id, this.settings).then(result => {
                this.settings = result.data
                this.$notify({
                    group: 'ops',
                    type: 'success',
                    title: 'Operation success',
                    text: 'Successfully updated the player settings.'
                })
            }).catch(error => {
                this.$notify({
                    group: 'ops',
                    type: 'error',
                    title: 'Operation failed',
                    text: 'Failed to updated the player settings.'
                })
            })
        }
    }
}
</script>